import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import { useDispatch, useSelector } from 'react-redux';
import {
	clearSharepointError,
	setSharepointError,
	setFolderContextLoading,
	setItemId,
	setLoading,
	setParentItemId,
	triggerRefresh,
} from '@/features/table/attachmentsTableSlice';
import { determineIfFolderDeletable, noteFolders } from '@/utils/folders';
import { AddFolderSharepointDialog } from '@/components/dialogs/AddFolderSharepointDialog';
import { MoveFileDialog } from '@/components/dialogs/MoveFileDialog';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { showToast } from '@/features/toast/toastSlice';
import { FileUploadMSGraph } from '@/components/FileUploadMSGraph';
import { specialChars } from '@/utils/constants';
import getCorrectFolderOwner from '@/hooks/getCorrectFolderOwner';
import {
	useDeleteSharepointItemMutation,
	useGetBreadcrumbsQuery,
	useGetDriveItemQuery,
	useGetItemsInfiniteQuery,
	useMoveSharepointItemMutation,
	invalidateGetItemsForParent,
} from '@/features/msGraph/msGraphApi';
import { getSharepointError } from '@/features/msGraph/helper';
// import { buildSupportTicketPrefill } from '@/components/dialogs/IssueReportingDialog/supportTicketPrefill';
import { useMsGraph } from '@/hooks/useMsGraph';
import store from '@/app/store/store';
import { Box, Button, Grid, Tooltip } from '@mui/material';
import { openDialog } from '@/features/dialog/dialogSlice';
import { Add, Warning } from '@mui/icons-material';

const initialState = {
	deal: {},
	client: {},
	sharepoint: {},
	isLoading: false,
	isFolderContextLoading: false,
	isError: false,
	errorMessage: '',
	dealRow: {},
	ownerId: null,
	files: [],
	allFiles: [],
	paginationModel: 0,
	setPaginationModel: (newModel) => {},
	count: 0,
	// handleNextPage: () => {},
	// handlePreviousPage: () => {},
	canCreate: false,
	clientFolderDriveId: null,
	promptFolderSelectOnUpload: false,
	handleDelete: async () => {},
	handleMove: async (newParentId) => {},
	handleUploadFiles: async (files) => {},
	refreshAttachmentsList: () => {},
	hasNextPage: false,
	hasPreviousPage: false,
	exact: false,
	setExact: (bool) => {},
	// handleRenameFile: async (item, newName) => {},
	// handleUpdateFileMetadata: async (item, metadata) => {},
};

const getFolderPaths = (folders) => {
	const folderMap = folders.reduce((acc, folder) => {
		acc[folder.id] = folder;
		return acc;
	}, {});

	const buildPath = (folder) => {
		const { name, parentReference } = folder;

		if (!parentReference || !parentReference.id) {
			return name;
		}

		const parentFolder = folderMap[parentReference.id];
		if (parentFolder) {
			return `${buildPath(parentFolder)}/${name}`;
		}

		return name;
	};

	return folders.map((folder) => buildPath(folder));
};

const AttachmentsContext = createContext(null);

export const useAttachmentsContext = () => {
	const context = useContext(AttachmentsContext);
	if (!context) {
		throw new Error('useAttachmentsContext must be used within an AttachmentsContextProvider');
	}
	return context;
};

export const AttachmentsContextProvider = ({
	deal,
	client,
	relationshipCompany,
	displayCreateFolder = true,
	promptFolderSelectOnUpload = false,
	renderUpload = true,
	children,
}) => {
	const dispatch = useDispatch();

	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

	const primaryEntity = relationshipCompany ?? deal;

	const {
		correctOwner: ownerId,
		loading: loadingOwner,
	} = getCorrectFolderOwner({
		deal,
		client: client?.clientRow,
		relationshipCompany,
	});

	const [exact, setExact] = useState(false);

	const isRelationshipCompany = Boolean(relationshipCompany);

	const ownerQuery = useGetUserDetailsQuery({ userId: ownerId }, { skip: !ownerId });

	const clientRow = client?.clientRow;
	// A relationship company takes priority, then a deal, then a client.
	const isClient = !deal && !relationshipCompany;
	const dealId = deal?.dealId;
	const clientId = clientRow?.clientId;
	const objectId = relationshipCompany?.id ?? (isClient ? clientId : dealId);

	const dealRowQuery = useGetDealRowQuery(dealId, {
		skip: !dealId,
	});

	const resolvedDealRow = relationshipCompany ?? dealRowQuery.data;

	const clientFolderDriveId =
		resolvedDealRow?.clientFolderDriveId ?? clientRow?.clientFolderDriveId;
	// const showFolderView = true;

	const { refreshIndicator, itemId, parentItemId, searchValue, selectedRows, loading } =
		useSelector((state) => state.attachmentsTable);

	const [moveSharepointItem] = useMoveSharepointItemMutation();
	const [deleteSharepointItem] = useDeleteSharepointItemMutation();

	const { uploadFilesAndRetry } = useMsGraph();

	const isFolderContextLoading =
		loadingOwner ||
		ownerQuery.isLoading ||
		(Boolean(ownerId) && ownerQuery.isUninitialized) ||
		(primaryEntity &&
			!isRelationshipCompany &&
			(dealRowQuery.isLoading || dealRowQuery.isUninitialized));

	useEffect(() => {
		dispatch(setFolderContextLoading(isFolderContextLoading));
		return () => dispatch(setFolderContextLoading(false));
	}, [dispatch, isFolderContextLoading]);

	const sharepoint = useMemo(() => {
		const isValidSharepoint =
			ownerQuery.data?.sharepointSite != null &&
			ownerQuery.data?.clientSiteSuffix != null &&
			ownerQuery.data?.clientFolder != null &&
			ownerQuery.data?.clientSite != null;
		return {
			sharepointSite: ownerQuery.data?.sharepointSite,
			clientSiteSuffix: ownerQuery.data?.clientSiteSuffix,
			clientFolder: ownerQuery.data?.clientFolder,
			clientSite: ownerQuery.data?.clientSite,
			isValid: isValidSharepoint,
		};
	}, [ownerQuery.data]);

	const baseSite = useMemo(() => {
		if (sharepoint?.clientSite && sharepoint?.clientFolder) {
			try {
				return decodeURI(`${sharepoint.clientSite}/${sharepoint.clientFolder}`);
			} catch (e) {
				console.log('Error decoding sharepoint URL', e);
				return `${sharepoint.clientSite}/${sharepoint.clientFolder}`;
			}
		}
		return '';
	}, [sharepoint?.clientSite, sharepoint?.clientFolder]);

	const hasRelationshipCompany = Boolean(relationshipCompany);
	const hasDeal = Boolean(deal);

	useEffect(() => {
		const rowId = hasRelationshipCompany
			? relationshipCompany?.driveId
			: hasDeal
				? dealRowQuery.data?.driveId
				: clientRow?.driveId;

		dispatch(setItemId(rowId));
		dispatch(setParentItemId(rowId));
	}, [
		hasRelationshipCompany,
		hasDeal,
		clientRow?.driveId,
		dealRowQuery.data?.driveId,
		relationshipCompany?.driveId,
		dispatch,
	]);

	const driveQuery = useGetDriveItemQuery(
		{
			itemId,
			driveId: clientFolderDriveId,
		},
		{ skip: !itemId || !clientFolderDriveId },
	);

	const [paginationModel, setPaginationModel] = useState({
		pageSize: 10,
		page: 0,
	});

	const [sortModel, setSortModel] = useState([{ field: 'lastModifiedDateTime', sort: 'desc' }]);

	const {
		data,
		fetchNextPage,
		fetchPreviousPage,
		isFetchingNextPage,
		isFetchingPreviousPage,
		hasNextPage,
		hasPreviousPage,
		...infiniteQuery
	} = useGetItemsInfiniteQuery(
		{
			itemId,
			driveId: clientFolderDriveId,
			orderBy: sortModel[0].field,
			direction: sortModel[0].sort,
			searchText: searchValue,
			exact,
		},
		{ skip: !itemId || !clientFolderDriveId },
	);

	const { data: breadcrumbs } = useGetBreadcrumbsQuery(
		{
			endItemId: itemId,
			startItemId: parentItemId,
			driveId: clientFolderDriveId,
		},
		{ skip: !itemId || !parentItemId || !clientFolderDriveId },
	);

	const sharepointErrors = useSelector((state) => state.attachmentsTable.sharepointErrors);

	const querySharepointError = getSharepointError(infiniteQuery.error || driveQuery.error);
	const isError = sharepointErrors.length > 0 || Boolean(querySharepointError);
	const errorMessage =
		sharepointErrors[sharepointErrors.length - 1]?.errorMessage ??
		querySharepointError?.errorMessage ??
		'';

	useEffect(() => {
		if (infiniteQuery.isSuccess) {
			dispatch(clearSharepointError());
		}
	}, [dispatch, infiniteQuery.isSuccess]);

	useEffect(() => {
		if (!objectId || !querySharepointError) {
			return;
		}
		if (sharepointErrors.some((item) => item.objectId === objectId)) {
			return;
		}

		dispatch(setSharepointError({ ...querySharepointError, objectId }));
		// Toast can fire before SharePoint finishes (e.g. slow drive fetch that later succeeds).
		// dispatch(
		// 	showToast({
		// 		id: 'sharepoint-documents-error',
		// 		message: querySharepointError.error,
		// 		autohide: false,
		// 		action: {
		// 			label: 'Report Issue',
		// 			dialogName: 'issueReporting',
		// 			data: buildSupportTicketPrefill({
		// 				subject: querySharepointError.error,
		// 				errorMessage: querySharepointError.errorMessage,
		// 			}),
		// 		},
		// 	}),
		// );
	}, [dispatch, objectId, querySharepointError, sharepointErrors]);

	useEffect(() => {
		if (paginationModel.page != 0) {
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
		}
	}, [itemId, sortModel]);

	const count = useMemo(() => {
		const sum = (data?.pages ?? []).reduce((acc, page) => {
			acc += (page?.folders ?? []).length + (page?.files ?? []).length;
			return acc;
		}, 0);
		if (searchValue.length === 0) {
			return driveQuery.data?.folder?.childCount ?? 0;
		} else if (hasNextPage && paginationModel.page + 1 === data?.pages?.length) {
			return -1;
		} else {
			return sum;
		}
	}, [driveQuery.data?.folder?.childCount, paginationModel.page, searchValue, hasNextPage]);

	const mapFile = useCallback(
		(f, folderPaths) => {
			const suffix = f.name.split('.');
			const isFolder = !Object.keys(f).includes('file');

			const fileInsideNoteFolder =
				breadcrumbs?.some((b) => {
					const name = b.name.normalize();
					try {
						return noteFolders.includes(decodeURI(name));
					} catch (e) {
						return noteFolders.includes(name);
					}
				}) &&
				(!isFolder || f.folder?.childCount);

			const cannotDelete = determineIfFolderDeletable(f) || fileInsideNoteFolder;

			// console.log('FILE TTTT', { f, folderPaths, cannotDelete, fileInsideNoteFolder });

			let webUrl;
			try {
				webUrl = decodeURI(f.webUrl).replace(baseSite, '').replace(decodeURI(f.name), '');
			} catch (e) {
				console.log('Error decoding uri', e);
			}

			const parentPath = f.parentReference?.path
				? f.parentReference.path.split('/root:')
				: null;
			const directory =
				folderPaths.find((p) => p.endsWith(f.parentReference.name)) ??
				(parentPath ? parentPath[parentPath.length - 1] : webUrl) ??
				'';

			return {
				...f,
				type: Object.keys(f).includes('file') ? suffix[suffix.length - 1] : '000folder',
				description: f?.description ?? '',
				directory,
				createdDateTimeUnix: Date.parse(f.createdDateTime),
				lastModifiedTimeUnix: Date.parse(f.lastModifiedDateTime),
				cannotDelete,
				isFolder,
			};
		},
		[breadcrumbs],
	);

	const infiniteFiles = useMemo(() => {
		const page = paginationModel.page;
		console.log('infiniteQuery.data?.pages', page, data?.pages, data?.pages[page]);
		const { files: newFiles, folders } = data?.pages[page] ?? {
			files: [],
			folders: [],
		};

		console.log('INFINITE FILES AND FOLDERS', { page, files: newFiles, folders });
		const filesAndFolders = newFiles.concat(folders);
		const folderPaths = getFolderPaths(folders);

		return filesAndFolders.map((f) => mapFile(f, folderPaths));
	}, [data?.pages, paginationModel.page]);

	const allFiles = useMemo(() => {
		return (data?.pages ?? []).reduce((acc, dataPage) => {
			const { files: newFiles, folders } = dataPage ?? {
				files: [],
				folders: [],
			};
			const filesAndFolders = newFiles.concat(folders);
			const folderPaths = getFolderPaths(folders);
			acc.push(filesAndFolders.map((f) => mapFile(f, folderPaths)));
			return acc;
		}, []);
	}, [data?.pages]);

	const handlePaginationModelChange = async (newModel) => {
		const page = paginationModel.page;
		const newPage = newModel?.page;
		console.log('[handlePaginationModelChange] New page', {
			newPage,
			newModel,
			page,
			paginationModel,
			data,
			hasNextPage,
			hasPreviousPage,
		});
		if (newPage > page && !data?.pages[newPage]) {
			await fetchNextPage();
		} else if (newPage < page && !data?.pages[newPage]) {
			await fetchPreviousPage();
		}
		setPaginationModel((prev) => ({ ...prev, page: newPage }));
	};

	const handleSortModelChange = async (newModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		setSortModel(newModel);
	};

	const refreshAttachmentsList = useCallback(() => {
		invalidateGetItemsForParent(dispatch, store.getState, {
			driveId: clientFolderDriveId,
			itemId,
		});
	}, [clientFolderDriveId, itemId, dispatch]);

	useEffect(() => {
		if (refreshIndicator === 0) return;
		refreshAttachmentsList();
	}, [refreshIndicator, refreshAttachmentsList]);

	const handleDelete = async () => {
		const itemLength = selectedRows.length;
		await Promise.all(
			selectedRows.map(
				async (item) =>
					await deleteSharepointItem({
						driveId: item.parentReference.driveId,
						itemId: item.id,
					}).unwrap(),
			),
		)
			.then((res) => {
				console.log('handleDelete RES', res);
				dispatch(
					showToast({
						message: `Successfully deleted ${itemLength} item${
							itemLength > 1 ? 's' : ''
						}.`,
						success: true,
					}),
				);
				dispatch(triggerRefresh());
			})
			.catch((err) => {
				console.log('Error deleting items', err);
				const message =
					err?.response?.data?.error?.message ??
					err?.message ??
					'Error deleting items; please try again.';
				dispatch(
					showToast({
						message: `Error: ${message}`,
						error: true,
					}),
				);
			})
			.finally(() => setConfirmDialogOpen(false));
	};

	const handleMove = async (newParentId) => {
		return await Promise.all(
			selectedRows.map(
				async (item) =>
					await moveSharepointItem({
						driveId: item.parentReference.driveId,
						itemId: item.id,
						newParentId,
					}).unwrap(),
			),
		);
	};

	const handleUploadFiles = useCallback(
		async (files, uploadItemId = itemId) => {
			const fileList = (Array.isArray(files) ? files : [files]).filter(Boolean);
			if (fileList.length === 0) {
				return { success: true, uploadedCount: 0, failedCount: 0 };
			}

			dispatch(setLoading(true));
			try {
				const result = await uploadFilesAndRetry({
					driveId: clientFolderDriveId,
					itemId: uploadItemId,
					files: fileList,
				});

				if (result.success && (searchValue.length > 0 || !result.cachePatched)) {
					refreshAttachmentsList();
				}

				return result;
			} catch (e) {
				console.log('Error uploading files', e);
				return { success: false, uploadedCount: 0, failedCount: fileList.length };
			} finally {
				dispatch(setLoading(false));
			}
		},
		[
			clientFolderDriveId,
			itemId,
			uploadFilesAndRetry,
			dispatch,
			primaryEntity,
			client,
			searchValue,
			refreshAttachmentsList,
		],
	);
	const contextValue = {
		deal,
		client,
		relationshipCompany,
		ownerId,
		dealRow: resolvedDealRow ?? {},
		clientFolderDriveId,
		files: infiniteFiles,
		allFiles,
		paginationModel,
		setPaginationModel: handlePaginationModelChange,
		sortModel,
		setSortModel: handleSortModelChange,
		count,
		exact,
		setExact,

		isFolderContextLoading,
		isLoading:
			isFolderContextLoading ||
			infiniteQuery.isFetching ||
			isFetchingNextPage ||
			isFetchingPreviousPage ||
			loading,
		isError,
		errorMessage,
		sharepoint,
		handleDelete: () => setConfirmDialogOpen(true),
		handleMove,
		handleUploadFiles,
		refreshAttachmentsList,
		hasNextPage: hasNextPage || paginationModel.page + 1 < (data?.pages?.length ?? 0),
		hasPreviousPage: paginationModel.page > 0,
		canCreate: true,
		promptFolderSelectOnUpload,
	};

	return (
		// Provide the context variables to the children components.
		<AttachmentsContext.Provider value={contextValue}>
			{renderUpload && (
				<Grid container spacing={1}>
					<Grid size={{ sm: 12, md: displayCreateFolder ? 10 : 12 }}>
						<FileUploadMSGraph />
					</Grid>
					{displayCreateFolder && (
						<Grid size={{ sm: 12, md: 2 }}>
							<Box
								display='flex'
								justifyContent='flex-end'
								alignItems='center'
								py={1}
								width={'100%'}
							>
								<Tooltip
									title={
										isFolderContextLoading ||
										!itemId ||
										loading ||
										searchValue.length > 0
											? 'Sorry, you cannot create a folder here.'
											: null
									}
								>
									<span>
										<Button
											disabled={
												isFolderContextLoading ||
												!itemId ||
												searchValue.length > 0
											}
											variant='tab_header'
											startIcon={<Add />}
											onClick={() =>
												dispatch(openDialog({ dialogName: 'createFolder' }))
											}
										>
											Create folder
										</Button>
									</span>
								</Tooltip>
							</Box>
						</Grid>
					)}
				</Grid>
			)}
			{/* <Typography>{`Page ${page}`}</Typography>
			<Stack direction='row' justifyContent={'space-between'} alignItems={'center'}>
				{page > 0 && (
					<Button variant='outlined' onClick={handlePreviousPage}>
						Previous
					</Button>
				)}
				{infiniteQuery.hasNextPage && (
					<Button variant='outlined' onClick={handleNextPage}>
						Next
					</Button>
				)}
			</Stack> */}

			{children}
			<MoveFileDialog />
			<AddFolderSharepointDialog />
			<ConfirmDialog
				openDialog={confirmDialogOpen}
				handleClose={() => setConfirmDialogOpen(false)}
				handleConfirm={handleDelete}
				actionName={'delete'}
			/>
		</AttachmentsContext.Provider>
	);
};

export default AttachmentsContext;